import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Dialog as SheetPrimitive } from 'radix-ui';
import { cn } from '@worldcoo/ui/lib/utils';
import { observeSnapScroller, settledDismissed, snapProgress } from '@/lib/snap-scroller';

/**
 * A full-screen sheet whose DISMISS GESTURE IS A NATIVE SCROLL.
 *
 * The panel is the second slot of a two-slot horizontal scroll-snap container, parked on itself, so
 * swiping it away IS scrolling toward the empty slot — the drag and its momentum settle run on the
 * compositor with the browser's own fling physics, and no React commit can stutter them. Nothing here
 * interpolates per frame; the only main-thread work is reading the scroll position back so the
 * backdrop can fade with the drag (see `@/lib/snap-scroller`).
 *
 * It replaces a `vaul` drawer, which drove the same gesture from pointer handlers on the main thread
 * and cost this page ~10.4KB brotli. Radix's Dialog — already on the landing page's first-paint chunk,
 * and what `vaul` itself was built on — keeps the focus trap, focus restoration, Escape, `aria-modal`,
 * the inert background and the page-scroll lock. Only the gesture is ours.
 */

const SheetTitle = SheetPrimitive.Title;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
	return <SheetPrimitive.Root data-slot="snap-sheet" {...props} />;
}

/**
 * The backdrop and the panel are rendered as the Portal's OWN children, not wrapped in a component of
 * their own. `Dialog.Portal` gives each child its own `Presence`, and a `Presence` measures the exit
 * animation on the DOM node its child renders — an intermediate component returning a fragment gives it
 * no node, so it unmounted on the spot and the close never animated.
 */
function SheetContent({
	className,
	panelClassName,
	children,
	onDismiss,
	...contentProps
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
	onDismiss: () => void;
	/**
	 * Classes for the visible PANEL. The surface a caller paints is this one, never the scroller —
	 * that is the mechanism, and a background on it would fill the empty slot too, so a drag would
	 * reveal an opaque nothing instead of the page behind.
	 */
	panelClassName?: string;
}) {
	// The node in STATE, not a ref: the gesture is wired up in an effect, and an effect keyed on a ref
	// object never re-runs when the node arrives.
	const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
	const backdropRef = useRef<HTMLDivElement>(null);
	/**
	 * Dismissed BY THE FINGER, so there is nothing left to animate away — the scroll already carried
	 * the panel off screen. Held in state rather than on the node because dropping the exit animation
	 * is what stops the backdrop flashing its full dim back: a running animation overrides the inline
	 * opacity the drag was writing, so `fade-out-0` would start from 1 rather than from where the
	 * gesture left it. With no animation to wait for, Radix also unmounts immediately.
	 */
	const [dragDismissed, setDragDismissed] = useState(false);
	// Cleared as the next sheet's node arrives: this component now outlives a close, so a flag set by
	// one dismissal would otherwise strip the exit animation off every close that followed it.
	const attachScroller = useCallback((node: HTMLDivElement | null) => {
		setScroller(node);
		if (node) setDragDismissed(false);
	}, []);

	// Park on the panel BEFORE the first paint: a fresh scroller starts at 0, which is the dismissed
	// slot, so anything later than a pre-paint write flashes the sheet in its gone position.
	useLayoutEffect(() => {
		if (!scroller) return;
		scroller.scrollLeft = scroller.scrollWidth - scroller.clientWidth;
	}, [scroller]);

	useLayoutEffect(() => {
		if (!scroller) return;

		const maxScroll = () => scroller.scrollWidth - scroller.clientWidth;
		const progress = () => snapProgress(scroller.scrollLeft, maxScroll());
		/** Snap should have taken it home; a resize mid-gesture can leave it a pixel or two off, and a
		 *  scroller parked off-rest never travels its full distance on the next drag. */
		const repark = () => {
			const rest = maxScroll();
			if (Math.abs(scroller.scrollLeft - rest) > 1) scroller.scrollLeft = rest;
		};
		let scrolling = false;

		const gesture = observeSnapScroller(scroller, {
			onStart: () => {
				scrolling = true;
			},
			// While the finger owns the panel the backdrop must TRACK it, not chase it through a
			// transition — so the level is written straight onto the node.
			onFrame: () => {
				const backdrop = backdropRef.current;
				if (backdrop) backdrop.style.opacity = (1 - progress()).toFixed(3);
			},
			onSettle: () => {
				scrolling = false;
				if (settledDismissed(progress())) {
					setDragDismissed(true);
					onDismiss();
					return;
				}
				repark();
				const backdrop = backdropRef.current;
				if (backdrop) backdrop.style.removeProperty('opacity');
			},
		});

		// The panel's width changes the travel distance (an orientation change, a resize), and the
		// scroller has to stay parked on it. Never while a scroll is in flight — repositioning under a
		// fling eats the gesture.
		const observer = new ResizeObserver(() => {
			if (scrolling) return;
			repark();
		});
		observer.observe(scroller);

		return () => {
			gesture.stop();
			observer.disconnect();
		};
	}, [scroller, onDismiss]);

	return (
		<SheetPrimitive.Portal>
			<SheetPrimitive.Overlay
				ref={backdropRef}
				data-slot="snap-sheet-backdrop"
				className={cn(
					'bg-black/10 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 z-40 duration-200',
					dragDismissed
						? 'animate-none'
						: 'data-open:animate-in data-closed:animate-out data-open:fade-in-0 data-closed:fade-out-0',
					// `!`: a bare `motion-reduce:animate-none` ties with the `data-open:` utilities on
					// specificity and loses on source order, so the slide and the fade still ran.
					'motion-reduce:animate-none!',
				)}
			/>
			<SheetPrimitive.Content
				// Radix hides the background from assistive tech with `aria-hidden`, which is the robust half,
				// but sets no `aria-modal` of its own — and the APG asks for both on a dialog that covers the
				// viewport.
				aria-modal
				data-slot="snap-sheet-content"
				className={cn(
					// THE SCROLLER: two full-width snap slots side by side, parked on the second one.
					// `overscroll-x-none` is load-bearing — without it the drag chains out at the boundary
					// and iOS takes it as the back-swipe navigation gesture.
					'fixed inset-0 z-50 flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-none outline-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
					'duration-300',
					dragDismissed
						? 'animate-none'
						: 'data-open:animate-in data-closed:animate-out data-open:slide-in-from-right data-closed:slide-out-to-right',
					'motion-reduce:animate-none!',
					className,
				)}
				{...contentProps}
				// AFTER the spread, and a callback rather than a ref object. React 19 makes `ref` an ordinary
				// prop, so `ComponentProps<typeof Content>` carries it and the rest-spread reintroduces it as
				// `undefined` — placed first, this ref was silently overwritten and the whole gesture never
				// attached, leaving a sheet that scrolled away and then sat there.
				ref={attachScroller}
			>
				{/* What the panel scrolls away INTO. `aria-hidden` and inert: it carries no content and no
				    tap target — the panel covers the viewport, so a visitor never sees a backdrop to press,
				    and closing is the X button, Escape or the swipe. */}
				<div aria-hidden data-slot="snap-sheet-dismiss-area" className="h-full w-full shrink-0 snap-start" />
				{/* `overscroll-y-contain` here, unlike the bottom sheet this was ported from: there the panel
				    and the snap scroller shared an axis, so a downward drag at the top of the panel had to
				    CHAIN OUT to the scroller — that chaining was the gesture. Here the gesture is horizontal,
				    so the panel's vertical scroll must not chain anywhere. */}
				<div
					data-slot="snap-sheet-panel"
					// `relative`: the panel is what decoration inside it is positioned against. The old drawer's
					// panel was a `fixed` box, so an `absolute inset-0` texture sized to it; here the panel is a
					// scroll slot, and without this the nearest positioned ancestor is the SCROLLER — which put
					// the menu's diamond pattern on the empty slot, 390px off screen.
					className={cn(
						'relative flex h-full w-full shrink-0 snap-end flex-col overflow-y-auto overscroll-y-contain',
						panelClassName,
					)}
				>
					{children}
				</div>
			</SheetPrimitive.Content>
		</SheetPrimitive.Portal>
	);
}

export { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger };
